import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Datatable} from '../../../components/Datatable/Datatable'
import type {MetaType} from '../../../components/Datatable/Datatable'
import {Button} from '../../../components/Button/Button'
import useModals from '../../../hooks/useModals'
import {DELETE, GET, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {ModalDelete} from '../../../components/Modals/General/ModalDelete/ModalDelete'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {HotelActivityType, getCategoryLabel, getAgeGroupLabel} from './types'

const HotelActivitiesContent: FC = () => {
  const navigate = useNavigate()
  const {showDeleteModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [activities, setActivities] = useState<HotelActivityType[]>([])
  const [id, setId] = useState<number | null>(null)
  const [, setMeta] = useState<MetaType | undefined>(undefined)

  function showPrice(value: string) {
    if (value && parseFloat(value) > 0) {
      return `R$ ${parseFloat(value).toFixed(2)}`
    }
    return i18n.t('general.paymentFree')
  }

  const getActivities = async () => {
    try {
      setLoading(true)
      const res = await GET('/hotel-activities')
      const formattedActivities = res.data.map((activity: any) => ({
        id: activity.id,
        title: activity.title,
        description: activity.description,
        time: activity.time,
        location: activity.location,
        period: activity.period,
        capacity: activity.capacity,
        order: activity.order,
        image: activity.image,
        category: activity.category,
        ageGroup: activity.ageGroup,
        price: showPrice(activity.price),
        requiresReservation: activity.requiresReservation,
        isActive: activity.isActive,
        pluginspaceId: activity.pluginspaceId,
        categoryLabel: getCategoryLabel(activity.category),
        ageGroupLabel: activity.ageGroup ? getAgeGroupLabel(activity.ageGroup) : '-',
        priceFormatted: activity.price ? `R$ ${Number(activity.price).toFixed(2)}` : 'Gratuito',
        reservationLabel: activity.requiresReservation ? 'Sim' : 'Não',
        statusLabel: activity.isActive ? 'Ativo' : 'Inativo',
        shortDescription:
          activity.description.length > 50
            ? activity.description.substring(0, 50) + '...'
            : activity.description,
        pluginSpaceName: activity.pluginSpace?.name ?? '',
      }))
      setActivities(formattedActivities)
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const deleteActivity = async () => {
    try {
      setLoading(true)
      const response = await DELETE(`/hotel-activities/${id}`)
      if (response.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_activities.msg_success_delete')),
          text: String(i18n.t('hospitality_activities.msg_success_delete_text')),
        }).then(async () => await getActivities())
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hospitality_activities.msg_error_delete')),
          text: String(i18n.t('hospitality_activities.msg_error_delete_text')),
        }).then(async () => await getActivities())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_delete')),
        text: String(i18n.t('hospitality_activities.msg_error_delete_text')),
      }).then(async () => await getActivities())
      console.log(e)
    }
  }

  useEffect(() => {
    getActivities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const switchStatus = async (activity: HotelActivityType, status: boolean) => {
    try {
      const switchProduct = await PUT(`/hotel-activities/${activity.id}`, {
        isActive: !status,
      })
      if (switchProduct.success) {
        const newProducts = activities.map((product) => {
          if (product.id === activity.id) {
            product.isActive = !status
          }
          return product
        })
        setActivities(newProducts)
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_activities.msg_success_update')),
          text: String(i18n.t('hospitality_activities.msg_success_update')),
        })
      }
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_update')),
        text: String(i18n.t('hospitality_activities.msg_error_update')),
      })
      console.log(e)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_activities.list_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_activities.list_subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('hospitality_activities.btn_create')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('criar')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={activities}
                headers={[
                  {
                    key: i18n.t('hospitality_activities.field_title'),
                    value: 'title',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_category'),
                    value: 'categoryLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_order'),
                    value: 'order',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_age_group'),
                    value: 'ageGroupLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_time'),
                    value: 'time',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_location'),
                    value: 'location',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_period'),
                    value: 'period',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_capacity'),
                    value: 'capacity',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_price'),
                    value: 'priceFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_activities.field_requires_reservation'),
                    value: 'reservationLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('general.active'),
                    value: 'isActive',
                    type: 'check',
                    action: (item) => switchStatus(item, item.isActive),
                  },
                  {key: i18n.t('general.actions'), value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      navigate(`editar/${item.id}`)
                    },
                    title: i18n.t('hospitality_activities.btn_edit'),
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(i18n.t('hospitality_activities.btn_delete'))
                      setId(item.id)
                    },
                    title: i18n.t('hospitality_activities.btn_delete'),
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteActivity()} />
        </div>
      )}
    </>
  )
}

const HotelActivities: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: 'Atividades do Hotel', path: 'hotel-activities', isActive: true},
        ]}
      >
        Atividades do Hotel
      </PageTitle>
      <HotelActivitiesContent />
    </>
  )
}

export {HotelActivities}
